import React from "react"
import classNames from "classnames"
import { ArrowDownIcon } from "../../icons"
import { InputVariant } from "@punks/core"
import classes from "./CustomSelect.module.css"
import { useDetectClickOutside } from "../../../features/ui/hooks/useDetectClickOutside"

export type SelectSize = "small" | "medium" | "large"

export interface SelectItem {
  id: string
  label: React.ReactNode
  disabled?: boolean
  className?: string
}

export interface SelectClasses {
  root?: string
  select?: string
  panel?: string
  option?: string
}

export interface CustomSelectProps {
  id?: string
  name?: string
  // color: ThemeColor
  // borderColor: ThemeColor
  // negative?: boolean
  label?: React.ReactNode
  required?: boolean
  variant?: InputVariant
  // autoComplete?: string
  size?: SelectSize
  fullWidth?: boolean
  disabled?: boolean
  fixed?: boolean
  // error?: boolean
  control?: any
  inputRef?: React.Ref<any>
  // defaultValue?: string
  value?: string
  onChange?: (newValue: string) => void
  values: SelectItem[]
  // emptyValue?: boolean
  className?: string
  selectClassName?: string
  itemClassName?: string
  // noBorder?: boolean
  classes?: SelectClasses
  zIndex?: number
}

const isSelected = (item: SelectItem, value?: string) => item.id === value

const CustomSelect = ({
  id,
  name,
  // color,
  // borderColor,
  size,
  label,
  required,
  variant,
  // autoComplete,
  fullWidth,
  disabled,
  fixed,
  // error,
  inputRef,
  value,
  // defaultValue,
  onChange,
  className,
  selectClassName,
  values,
  // emptyValue,
  itemClassName,
  // noBorder,
  classes: userClasses,
  zIndex,
}: CustomSelectProps) => {
  const [open, setOpen] = React.useState(false)
  const openToggle = () => setOpen(!open)
  const selectedLabel = values.find((x) => isSelected(x, value))?.label

  const handleSelect = (value: SelectItem) => {
    setOpen(false)
    onChange?.(value.id)
  }

  const handleClicks = () => {
    setOpen(false)
  }

  const rootRef = useDetectClickOutside({
    onTriggered: handleClicks,
  })

  return (
    <div
      className={classNames(
        classes.root,
        {
          [classes.fullWidth]: fullWidth,
        },
        className,
        userClasses?.root
      )}
      ref={rootRef}
    >
      <input
        id={id}
        name={name}
        type="hidden"
        required={required}
        value={value}
        ref={inputRef}
      />
      <button
        disabled={disabled}
        className={classNames(
          classes.button,
          {
            [classes.fullWidth]: fullWidth,
            [classes.buttonFilled]: variant === "filled",
            [classes.buttonOutlined]: variant === "outlined",
            [classes.buttonLarge]: size === "large",
            [classes.buttonMedium]: size === "medium",
            [classes.buttonSmall]: size === "small",
            [classes.buttonFixed]: fixed,
          },
          selectClassName,
          userClasses?.select
        )}
        onClick={() => {
          if (!disabled && !fixed) {
            openToggle()
          }
        }}
      >
        {label && <div className={classes.label}>{label}</div>}
        <span className={classes.valueLabel}>{selectedLabel}</span>
        {!fixed && (
          <ArrowDownIcon
            className={classNames(classes.arrow, {
              [classes.arrowOpen]: open,
              [classes.arrowLarge]: size === "large",
            })}
          />
        )}
      </button>
      <ul
        className={classNames(
          classes.dropdownPanel,
          {
            [classes.dropdownPanelOpen]: open,
            [classes.dropdownPanelOutlined]: variant === "outlined",
          },
          userClasses?.panel
        )}
        style={{ zIndex }}
      >
        {values.map((x) => (
          <li
            key={x.id}
            className={classNames(
              classes.item,
              {
                [classes.itemSelected]: isSelected(x, value),
                [classes.itemLarge]: size === "large",
                [classes.itemDisabled]: x.disabled === true,
              },
              itemClassName,
              x.className,
              userClasses?.option
            )}
            onClick={(e) => {
              e.preventDefault()
              if (!x.disabled) {
                handleSelect(x)
              }
            }}
          >
            {x.label}
          </li>
        ))}
      </ul>
    </div>
  )
}

CustomSelect.defaultProps = {
  color: "neutral",
  borderColor: "neutral",
  variant: "filled",
  size: "small",
  rounding: "rounded",
  negative: false,
}

export default CustomSelect
